<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="800"
    content-class="po-add-quality-grade-dialog"
    @click:outside="close"
  >
    <v-card class="po-add-quality-grade-card" rounded="0">
      <v-toolbar color="primary" flat height="48" class="white--text">
        <v-toolbar-title class="font-weight-bold">
          {{ $t("memberDataSection.greenCoffee.addNewQualityGrade") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="po-add-quality-grade-card__body black--text">
        <v-form ref="form" lazy-validation>
          <v-row class="mb-1">
            <v-col cols="6" class="py-0 px-4">
              <label class="po-add-quality-grade__label">
                {{ $t("memberDataSection.purchaseOrderDetail.lotId") }}
              </label>
              <v-text-field
                v-model="form.lotId"
                outlined
                dense
                hide-details
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <div class="d-flex justify-space-between align-end">
                <label class="po-add-quality-grade__label">
                  {{ $t("memberDataSection.greenCoffee.quantity") }}
                  <span class="red--text">*</span>
                </label>
                <span class="po-add-quality-grade__unit">kg</span>
              </div>
              <v-text-field
                v-model.number="form.quantity"
                type="number"
                outlined
                dense
                hide-details="auto"
                :rules="rules.quantity"
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"
                @input="onBagFieldsInput"
              ></v-text-field>
            </v-col>

            <v-col cols="6" class="py-0 px-4">
              <label class="po-add-quality-grade__label">
                {{ $t("memberDataSection.greenCoffee.unitSizeKgBag") }}
                <span class="red--text">*</span>
              </label>
              <v-text-field
                v-model.number="form.unitSize"
                type="number"
                outlined
                dense
                hide-details="auto"
                :rules="rules.unitSize"
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"
                @input="onBagFieldsInput"
              ></v-text-field>
            </v-col>

            <v-col cols="12" class="px-4 pt-4 pb-2">
              <p class="po-add-quality-grade__summary-line mb-2">
                <span class="font-weight-bold">
                  {{ $t("memberDataSection.greenCoffee.totalBags") }}:
                </span>
                <span class="font-weight-bold">{{ totalBags }}</span>
              </p>
              <p class="po-add-quality-grade__summary-line mb-3">
                <span class="font-weight-bold">
                  {{ $t("memberDataSection.greenCoffee.leftoverQuantity") }}:
                </span>
                <span class="font-weight-bold">{{ leftoverQuantity }} kg</span>
              </p>
              <p class="po-add-quality-grade__desc mb-0">
                {{ $t("memberDataSection.greenCoffee.leftoverQuantityDesc") }}
              </p>
            </v-col>

            <v-col cols="12" class="py-0 px-4">
              <label class="po-add-quality-grade__label">
                {{ $t("memberDataSection.greenCoffee.qualityScore") }}
              </label>
              <v-select
                v-model="form.qualityScoreId"
                :items="qualityScores"
                item-text="name"
                item-value="id"
                outlined
                dense
                hide-details
                :placeholder="$t('memberDataSection.pOrder.makeSelection')"
              ></v-select>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="po-add-quality-grade-card__actions pa-4">
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="primary"
          height="40"
          width="112"
          class="mr-3 text-uppercase font-weight-bold"
          @click="close"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          height="40"
          width="112"
          class="white--text text-uppercase font-weight-bold"
          :loading="submitting"
          @click="submit"
        >
          {{ $t("submit") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { isKenyaClient } from "@/utils";

export default {
  name: "PurchaseOrderAddQualityGradeDialog",
  props: {
    value: { type: Boolean, default: false },
    lot: { type: Object, default: null },
    maxQuantity: { type: [Number, String], default: 0 },
    suggestedLotId: { type: String, default: "" },
    existingLotIds: { type: Array, default: () => [] },
    defaultUnitSize: { type: [Number, String], default: null },
    qualityScores: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
    gradeOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: this.value,
      submitting: false,
      form: {
        lotId: "",
        quantity: null,
        unitSize: null,
        qualityScoreId: null,
      },
      rules: {
        quantity: [
          (v) =>
            (v !== null && v !== "" && v !== undefined) ||
            this.$t("memberDataSection.greenCoffee.quantityRequired"),
          (v) =>
            Number(v) > 0 ||
            this.$t("memberDataSection.greenCoffee.quantityMustBePositive"),
          (v) => this.validateMaxQuantity(v),
        ],
        unitSize: [
          (v) =>
            (v !== null && v !== "" && v !== undefined) ||
            this.$t("memberDataSection.greenCoffee.unitSizeRequired"),
          (v) =>
            Number(v) > 0 ||
            this.$t("memberDataSection.greenCoffee.unitSizeMustBePositive"),
        ],
      },
    };
  },
  computed: {
    isKenyaClient() {
      return isKenyaClient();
    },
    isEditMode() {
      return Boolean(this.lot);
    },
    unitSizeKg() {
      const entered = Number(this.form.unitSize);
      if (entered > 0) return entered;
      const fromLot = Number(this.lot?.unitSize);
      if (fromLot > 0) return fromLot;
      const fallback = Number(this.defaultUnitSize);
      if (fallback > 0) return fallback;
      return this.isKenyaClient ? 60 : 40;
    },
    maxQuantityKg() {
      return Number(this.maxQuantity) || 0;
    },
    totalBags() {
      const lotKg = Number(this.form.quantity) || 0;
      const unit = this.unitSizeKg;
      if (!lotKg || !unit) return 0;
      return Math.floor(lotKg / unit);
    },
    leftoverQuantity() {
      const lotKg = Number(this.form.quantity) || 0;
      const unit = this.unitSizeKg;
      if (!lotKg || !unit) return 0;
      return lotKg % unit;
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) this.initFormFromLot();
    },
    visible(v) {
      this.$emit("input", v);
    },
    lot: {
      handler() {
        if (this.visible) this.initFormFromLot();
      },
      deep: true,
    },
  },
  methods: {
    validateMaxQuantity(v) {
      const max = this.maxQuantityKg;
      if (max > 0 && Number(v) > max) {
        return this.$t("memberDataSection.greenCoffee.totalGreenBeansExceeded");
      }
      return true;
    },
    onBagFieldsInput() {
      if (this.$refs.form) this.$refs.form.validate();
    },
    defaultUnitSizeKg() {
      const fallback = Number(this.defaultUnitSize);
      if (fallback > 0) return fallback;
      return this.isKenyaClient ? 60 : 40;
    },
    initFormFromLot() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      const lot = this.lot || {};
      this.form.lotId = this.isEditMode
        ? lot.lotId || ""
        : this.suggestedLotId || "";
      this.form.quantity =
        lot.quantity != null && lot.quantity !== ""
          ? Number(lot.quantity)
          : null;
      const unitSize =
        lot.unitSize != null && lot.unitSize !== ""
          ? Number(lot.unitSize)
          : this.defaultUnitSizeKg();
      this.form.unitSize = unitSize;

      const selectedQuality =
        this.qualityScores.find(
          (s) =>
            s.id === lot.qualityScoreId ||
            s.name === lot.qualityTitle ||
            s.name === lot.qualityScore
        ) || null;
      this.form.qualityScoreId = selectedQuality ? selectedQuality.id : null;
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
    resetForm() {
      this.form.lotId = "";
      this.form.quantity = null;
      this.form.unitSize = this.defaultUnitSizeKg();
      this.form.qualityScoreId = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    isDuplicateLotId() {
      const id = String(this.form.lotId || "").trim();
      if (!id) return false;
      const current = this.isEditMode ? String(this.lot.lotId || "").trim() : "";
      return (this.existingLotIds || []).some((existing) => {
        const normalized = String(existing || "").trim();
        return normalized && normalized === id && normalized !== current;
      });
    },
    submit() {
      if (!this.$refs.form.validate()) return;
      if (this.isDuplicateLotId()) {
        this.$notify({
          type: "error",
          text: this.$t("memberDataSection.greenCoffee.lotIdAlreadyExists"),
        });
        return;
      }
      const lotQty = Number(this.form.quantity) || 0;
      const unitSize = this.unitSizeKg;
      if (lotQty <= 0 || unitSize <= 0) return;
      if (this.maxQuantityKg > 0 && lotQty > this.maxQuantityKg) {
        this.$notify({
          type: "error",
          text: this.$t("memberDataSection.greenCoffee.totalGreenBeansExceeded"),
        });
        return;
      }

      this.submitting = true;
      try {
        const selectedQuality =
          this.qualityScores.find((s) => s.id === this.form.qualityScoreId) || {};
        const payload = {
          lotId: String(this.form.lotId || "").trim(),
          quantity: lotQty,
          unitSize,
          qualityScoreId: this.form.qualityScoreId,
          totalBags: this.totalBags,
          leftoverQuantity: this.leftoverQuantity,
          qualityTitle: selectedQuality.name || "",
          qualityScore: selectedQuality.name || "",
          label: null,
        };
        this.$emit("submit", payload);
        this.resetForm();
        this.close();
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.po-add-quality-grade-card {
  font-family: "Poppins", sans-serif;
  background: #ffffff;
}

.po-add-quality-grade-card__body {
  padding-top: 20px !important;
  padding-bottom: 8px !important;
}

.po-add-quality-grade-card__actions {
  background: #ffffff;
}

.po-add-quality-grade__label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
}

.po-add-quality-grade__unit {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  text-transform: uppercase;
}

.po-add-quality-grade__summary-line {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.87);
}

.po-add-quality-grade__desc {
  font-size: 13px;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 400;
}
</style>

<style>
.po-add-quality-grade-dialog > .v-card {
  background: #ffffff !important;
}
</style>
